/**
 * Centralized Font Configuration
 *
 * Single source of truth for fonts across:
 * - optiedge-kaze (frontend theme selector)
 * - optiedge-visual-editor (iframe preview)
 * - optiedge-dev-container-code-server (sidecar saves theme)
 */

type FontCategory = "sans-serif" | "serif" | "monospace";

interface FontDefinition {
  cssFamily: string;
  googleParam: string;
  category: FontCategory;
}

/**
 * Master font configuration
 * Add new fonts here and they'll be available everywhere
 */
const FONT_CONFIG: Record<string, FontDefinition> = {
  // Sans-serif fonts
  Inter: {
    cssFamily: "Inter, sans-serif",
    googleParam: "Inter:wght@400;500;600;700",
    category: "sans-serif",
  },
  Roboto: {
    cssFamily: "Roboto, sans-serif",
    googleParam: "Roboto:wght@400;500;700",
    category: "sans-serif",
  },
  "Open Sans": {
    cssFamily: "Open Sans, sans-serif",
    googleParam: "Open+Sans:wght@400;500;600;700",
    category: "sans-serif",
  },
  Poppins: {
    cssFamily: "Poppins, sans-serif",
    googleParam: "Poppins:wght@400;500;600;700",
    category: "sans-serif",
  },
  Montserrat: {
    cssFamily: "Montserrat, sans-serif",
    googleParam: "Montserrat:wght@400;500;600;700",
    category: "sans-serif",
  },
  Outfit: {
    cssFamily: "Outfit, sans-serif",
    googleParam: "Outfit:wght@400;500;600;700",
    category: "sans-serif",
  },
  "Plus Jakarta Sans": {
    cssFamily: "Plus Jakarta Sans, sans-serif",
    googleParam: "Plus+Jakarta+Sans:wght@400;500;600;700",
    category: "sans-serif",
  },
  "DM Sans": {
    cssFamily: "DM Sans, sans-serif",
    googleParam: "DM+Sans:wght@400;500;600;700",
    category: "sans-serif",
  },
  "IBM Plex Sans": {
    cssFamily: "IBM Plex Sans, sans-serif",
    googleParam: "IBM+Plex+Sans:wght@400;500;600;700",
    category: "sans-serif",
  },
  Nunito: {
    cssFamily: "Nunito, sans-serif",
    googleParam: "Nunito:wght@400;500;600;700",
    category: "sans-serif",
  },
  Barlow: {
    cssFamily: "Barlow, sans-serif",
    googleParam: "Barlow:wght@400;500;600;700",
    category: "sans-serif",
  },
  Lato: {
    cssFamily: "Lato, sans-serif",
    googleParam: "Lato:wght@400;700",
    category: "sans-serif",
  },
  Geist: {
    cssFamily: "Geist, Geist Fallback, sans-serif",
    googleParam: "Geist:wght@400;500;600;700",
    category: "sans-serif",
  },
  // Popular fonts
  Raleway: {
    cssFamily: "Raleway, sans-serif",
    googleParam: "Raleway:wght@400;500;600;700",
    category: "sans-serif",
  },
  Quicksand: {
    cssFamily: "Quicksand, sans-serif",
    googleParam: "Quicksand:wght@400;500;600;700",
    category: "sans-serif",
  },
  Ubuntu: {
    cssFamily: "Ubuntu, sans-serif",
    googleParam: "Ubuntu:wght@400;500;700",
    category: "sans-serif",
  },
  // Display/3D-style fonts
  Bungee: {
    cssFamily: "Bungee, sans-serif",
    googleParam: "Bungee:wght@400",
    category: "sans-serif",
  },
  Righteous: {
    cssFamily: "Righteous, sans-serif",
    googleParam: "Righteous:wght@400",
    category: "sans-serif",
  },
  Orbitron: {
    cssFamily: "Orbitron, sans-serif",
    googleParam: "Orbitron:wght@400;500;600;700",
    category: "sans-serif",
  },

  // Serif fonts
  Merriweather: {
    cssFamily: "Merriweather, serif",
    googleParam: "Merriweather:wght@400;700",
    category: "serif",
  },
  "Playfair Display": {
    cssFamily: "Playfair Display, serif",
    googleParam: "Playfair+Display:wght@400;500;600;700",
    category: "serif",
  },
  Lora: {
    cssFamily: "Lora, serif",
    googleParam: "Lora:wght@400;500;600;700",
    category: "serif",
  },
  "Source Serif 4": {
    cssFamily: "Source Serif 4, serif",
    googleParam: "Source+Serif+4:wght@400;500;600;700",
    category: "serif",
  },
  "Libre Baskerville": {
    cssFamily: "Libre Baskerville, serif",
    googleParam: "Libre+Baskerville:wght@400;700",
    category: "serif",
  },
  "Space Grotesk": {
    cssFamily: "Space Grotesk, serif",
    googleParam: "Space+Grotesk:wght@400;500;600;700",
    category: "serif",
  },
  "PT Serif": {
    cssFamily: "PT Serif, serif",
    googleParam: "PT+Serif:wght@400;700",
    category: "serif",
  },
  Gabriela: {
    cssFamily: "Gabriela, Geist Fallback",
    googleParam: "Gabriela:wght@400",
    category: "serif",
  },
  "Delius Swash Caps": {
    cssFamily: "Delius Swash Caps, serif",
    googleParam: "Delius+Swash+Caps:wght@400",
    category: "serif",
  },

  // Monospace fonts
  "JetBrains Mono": {
    cssFamily: "JetBrains Mono, monospace",
    googleParam: "JetBrains+Mono:wght@400;500;600;700",
    category: "monospace",
  },
  "Fira Code": {
    cssFamily: "Fira Code, monospace",
    googleParam: "Fira+Code:wght@400;500;600;700",
    category: "monospace",
  },
  "Source Code Pro": {
    cssFamily: "Source Code Pro, monospace",
    googleParam: "Source+Code+Pro:wght@400;500;600;700",
    category: "monospace",
  },
  "Geist Mono": {
    cssFamily: "Geist Mono, Geist Mono Fallback, monospace",
    googleParam: "Geist+Mono:wght@400;500;600;700",
    category: "monospace",
  },
  "IBM Plex Mono": {
    cssFamily: "IBM Plex Mono, monospace",
    googleParam: "IBM+Plex+Mono:wght@400;500;600;700",
    category: "monospace",
  },
  "Roboto Mono": {
    cssFamily: "Roboto Mono, monospace",
    googleParam: "Roboto+Mono:wght@400;500;600;700",
    category: "monospace",
  },
  "Space Mono": {
    cssFamily: "Space Mono, monospace",
    googleParam: "Space+Mono:wght@400;700",
    category: "monospace",
  },
};

/**
 * Google Fonts URL parameters
 * Usage: `https://fonts.googleapis.com/css2?family=${GOOGLE_FONTS["Inter"]}&display=swap`
 */
const GOOGLE_FONTS: Record<string, string> = Object.fromEntries(
  Object.entries(FONT_CONFIG).map(([name, config]) => [name, config.googleParam])
);

/**
 * CSS font-family values
 * Usage: `font-family: ${CSS_FONTS["Inter"]}`
 */
const CSS_FONTS: Record<string, string> = Object.fromEntries(
  Object.entries(FONT_CONFIG).map(([name, config]) => [name, config.cssFamily])
);

/**
 * Get fonts by category
 */
function getFontsByCategory(category: FontCategory): Record<string, string> {
  return Object.fromEntries(
    Object.entries(FONT_CONFIG)
      .filter(([, config]) => config.category === category)
      .map(([name, config]) => [name, config.cssFamily])
  );
}

/**
 * Pre-computed category exports
 */
const SANS_SERIF_FONTS = getFontsByCategory("sans-serif");
const SERIF_FONTS = getFontsByCategory("serif");
const MONOSPACE_FONTS = getFontsByCategory("monospace");

/**
 * Default font values (system fallbacks)
 */
const DEFAULT_FONT_SANS =
  "'Geist', 'Geist Fallback', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

const DEFAULT_FONT_SERIF =
  '"Geist", "Geist Fallback", ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';

const DEFAULT_FONT_MONO =
  '"Geist Mono", "Geist Mono Fallback", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export type { FontCategory, FontDefinition };

export {
  FONT_CONFIG,
  GOOGLE_FONTS,
  CSS_FONTS,
  getFontsByCategory,
  SANS_SERIF_FONTS,
  SERIF_FONTS,
  MONOSPACE_FONTS,
  DEFAULT_FONT_SANS,
  DEFAULT_FONT_SERIF,
  DEFAULT_FONT_MONO,
};
